import { useState } from "react";
import { Send, Users, User, Phone, Mail, Smile, CalendarDays, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const AGES = ["0-5 ετών", "6-12 ετών", "13-17 ετών", "Ενήλικες"];
const EMPTY = { name: "", phone: "", email: "", child_name: "", age: "" };

const Field = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon size={22} strokeWidth={1.7} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" />
    {children}
  </div>
);
const inputCls = "w-full h-[52px] pl-14 pr-4 rounded-xl border border-slate-200 bg-white text-[15px] text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#3E63DD] focus:ring-2 focus:ring-[#3E63DD]/20";

const ModeBtn = ({ active, icon: Icon, label, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-14 rounded-xl border-2 inline-flex items-center justify-center gap-4 text-[15px] transition-colors ${active ? "border-[#3E63DD] bg-[#E8EEFD] text-[#3E63DD] font-bold" : "border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50"}`}>
    <Icon size={26} fill="currentColor" strokeWidth={1.5} className={active ? "text-[#3E63DD]" : "text-slate-600"} />{label}
  </button>
);

export const InterestDialog = ({ open, onOpenChange, business }) => {
  const [mode, setMode] = useState("child");
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/interest", { ...f, email: f.email.trim() || null, mode, business_id: business.id, business_name: business.name, child_name: mode === "child" ? f.child_name : "" });
      toast.success("Η εκδήλωση ενδιαφέροντος στάλθηκε! Θα επικοινωνήσουμε σύντομα μαζί σας.");
      setF(EMPTY); onOpenChange(false);
    } catch {
      toast.error("Η αποστολή απέτυχε. Δοκιμάστε ξανά.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[92vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-7 [&>button]:hidden" data-testid="interest-dialog">
        <button type="button" onClick={() => onOpenChange(false)} aria-label="Κλείσιμο" data-testid="interest-close" className="absolute right-5 top-5 text-slate-600 hover:text-slate-900"><X size={26} /></button>
        <div className="flex gap-5 pr-8">
          <span className="h-[84px] w-[84px] shrink-0 rounded-full bg-[#E8EEFD] grid place-items-center"><Send size={40} className="text-[#3E63DD] -ml-1" fill="currentColor" strokeWidth={1.4} /></span>
          <div>
            <DialogTitle className="font-display text-[28px] font-extrabold text-[#141B3D] leading-tight">Εκδήλωση Ενδιαφέροντος</DialogTitle>
            <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">Συμπληρώστε τα στοιχεία σας και η επιχείρηση θα επικοινωνήσει μαζί σας για πληροφορίες σχετικά με τα διαθέσιμα τμήματα, το πρόγραμμα και τις εγγραφές. Η αποστολή δεν αποτελεί εγγραφή και δεν σας δεσμεύει.</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="interest-form">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <ModeBtn active={mode === "child"} icon={Users} label="Για το παιδί μου" onClick={() => setMode("child")} testid="interest-mode-child" />
            <ModeBtn active={mode === "self"} icon={User} label="Για εμένα" onClick={() => setMode("self")} testid="interest-mode-self" />
          </div>
          <h4 className="font-display text-[17px] font-extrabold text-[#141B3D] pb-1">{mode === "child" ? "Στοιχεία γονέα / κηδεμόνα" : "Τα στοιχεία σας"}</h4>
          <Field icon={User}><input required value={f.name} onChange={set("name")} placeholder="Ονοματεπώνυμο *" className={inputCls} data-testid="interest-name" /></Field>
          <Field icon={Phone}><input required type="tel" value={f.phone} onChange={set("phone")} placeholder="Τηλέφωνο επικοινωνίας *" className={inputCls} data-testid="interest-phone" /></Field>
          <Field icon={Mail}><input type="email" value={f.email} onChange={set("email")} placeholder="Email (προαιρετικό)" className={inputCls} data-testid="interest-email" /></Field>
          {mode === "child" && (
            <>
              <h4 className="font-display text-[17px] font-extrabold text-[#141B3D] pt-4 pb-1">Στοιχεία παιδιού</h4>
              <Field icon={Smile}><input required value={f.child_name} onChange={set("child_name")} placeholder="Όνομα παιδιού *" className={inputCls} data-testid="interest-child-name" /></Field>
            </>
          )}
          <Field icon={CalendarDays}>
            <select required value={f.age} onChange={set("age")} data-testid="interest-age" className={`${inputCls} appearance-none cursor-pointer ${f.age ? "" : "text-slate-500"}`}>
              <option value="" disabled>Ηλικία *</option>
              {AGES.map((a) => <option key={a} value={a} className="text-slate-900">{a}</option>)}
            </select>
            <ChevronDown size={22} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" />
          </Field>
          <button type="submit" disabled={sending} data-testid="interest-submit" className="mt-3 w-full h-14 rounded-full bg-[#3E63DD] text-white text-lg font-bold inline-flex items-center justify-center gap-4 hover:brightness-110 disabled:opacity-60 transition-[filter,opacity]">
            <Send size={24} fill="currentColor" strokeWidth={1.4} /> {sending ? "Αποστολή..." : "Αποστολή"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
